import {
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  NumberInput,
  SegmentedControl,
  Slider,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createSearchProfile,
  deleteSearchProfile,
  getSearchProfiles,
  type LocationRules,
  type SearchProfile,
  updateSearchProfile,
  type WohnungCriteria,
} from '../lib/api.js';

const numOf = (v: number | string): number => (typeof v === 'number' ? v : parseFloat(v) || 0);

function ImportanceSlider({
  value,
  onChange,
  pct,
}: {
  value: number;
  onChange: (v: number) => void;
  pct?: number;
}): JSX.Element {
  return (
    <Group gap="sm" align="center" wrap="nowrap">
      <Text size="xs" c="dimmed" style={{ minWidth: 74 }}>
        Wichtigkeit
      </Text>
      <Slider
        style={{ flex: 1 }}
        value={value}
        onChange={onChange}
        min={0}
        max={3}
        step={0.25}
        color={value === 0 ? 'gray' : 'violet'}
        label={(v) => (v === 0 ? 'aus' : `${v}×`)}
        marks={[
          { value: 0, label: 'aus' },
          { value: 1, label: 'normal' },
          { value: 2, label: 'wichtig' },
          { value: 3, label: 'max' },
        ]}
      />
      {pct !== undefined && (
        <Badge
          variant="light"
          color={value === 0 ? 'gray' : 'violet'}
          style={{ minWidth: 48, fontVariantNumeric: 'tabular-nums' }}
        >
          {value === 0 ? 'aus' : `${pct}%`}
        </Badge>
      )}
    </Group>
  );
}

function Block({
  title,
  hint,
  importance,
  onImportance,
  pct,
  children,
}: {
  title: string;
  hint?: string;
  importance: number;
  onImportance: (v: number) => void;
  pct?: number;
  children?: ReactNode;
}): JSX.Element {
  return (
    <Stack gap={8} style={{ paddingBottom: 8, borderBottom: '1px solid rgba(15,23,42,0.06)' }}>
      <Text fw={600} size="sm">
        {title}
      </Text>
      {hint && (
        <Text size="xs" c="dimmed">
          {hint}
        </Text>
      )}
      {children}
      <ImportanceSlider value={importance} onChange={onImportance} pct={pct} />
    </Stack>
  );
}

const DEAL_LABEL: Record<string, string> = { miete: 'Miete', kauf: 'Kauf' };
const OBJ_LABEL: Record<string, string> = { wohnung: 'Wohnung', haus: 'Haus', grundstueck: 'Grundstück', egal: 'egal' };
const REGION_LABEL: Record<string, string> = { wien: 'Wien', umland: 'Umland', beide: 'Wien + Umland' };

function ProfileEditor({ profile }: { profile: SearchProfile }): JSX.Element {
  const qc = useQueryClient();
  const [cfg, setCfg] = useState<WohnungCriteria>(profile.config);
  const [loc, setLoc] = useState<LocationRules>(profile.location);
  const [excludedText, setExcludedText] = useState((profile.location.excludedDistricts ?? []).join(', '));

  const saveMut = useMutation({
    mutationFn: () => updateSearchProfile(profile.id, { config: cfg, location: loc }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['search-profiles'] });
      qc.invalidateQueries({ queryKey: ['objects'] });
      notifications.show({
        message: `Gespeichert · ${res.rescored} Objekte neu bewertet`,
        color: 'green',
      });
    },
    onError: (e) => notifications.show({ message: String(e), color: 'red' }),
  });

  const up = <K extends keyof WohnungCriteria>(k: K, patch: Partial<WohnungCriteria[K]>): void =>
    setCfg((c) => ({ ...c, [k]: { ...(c[k] as object), ...patch } }));

  const dirty = JSON.stringify(cfg) !== JSON.stringify(profile.config) || JSON.stringify(loc) !== JSON.stringify(profile.location);
  const isKauf = profile.dealType === 'kauf';

  const commitExcluded = (): void => {
    const nums = excludedText
      .split(/[,\s]+/u)
      .map((s) => parseInt(s, 10))
      .filter((n) => Number.isInteger(n) && n >= 1 && n <= 23);
    setLoc((l) => ({ ...l, excludedDistricts: nums }));
  };

  // Effektives Gewicht je Kriterium = Basisgewicht × Wichtigkeit, renormalisiert über
  // die aktiven Kriterien (importance > 0). Zeigt transparent die faire Verteilung.
  const base = profile.baseWeights;
  const impOf = (k: string): number =>
    k === 'location' ? loc.importance : ((cfg as unknown as Record<string, { importance?: number }>)[k]?.importance ?? 0);
  const activeKeys = Object.keys(base).filter((k) => impOf(k) > 0);
  const contribSum = activeKeys.reduce((s, k) => s + (base[k] ?? 0) * impOf(k), 0);
  const pctOf = (k: string): number => {
    if (!(k in base) || impOf(k) <= 0 || contribSum <= 0) return 0;
    return Math.round((((base[k] ?? 0) * impOf(k)) / contribSum) * 100);
  };
  const has = (k: string): boolean => k in base;

  // „Fair verteilen": aktive Kriterien auf gleiches effektives Gewicht bringen
  // (importance = Referenz / Basisgewicht). Kriterien auf „aus" bleiben aus.
  const fairVerteilen = (): void => {
    const active = Object.keys(base).filter((k) => impOf(k) > 0);
    if (active.length === 0) return;
    const ref = Math.min(...active.map((k) => base[k] ?? 1));
    const newCfg = { ...cfg } as unknown as Record<string, { importance?: number }>;
    let newLoc = { ...loc };
    for (const k of active) {
      const imp = Math.min(5, Math.max(0.1, Math.round((ref / (base[k] ?? 1)) * 100) / 100));
      if (k === 'location') newLoc = { ...newLoc, importance: imp };
      else newCfg[k] = { ...(newCfg[k] ?? {}), importance: imp };
    }
    setCfg(newCfg as unknown as WohnungCriteria);
    setLoc(newLoc);
  };

  return (
    <Stack gap="md" mt="sm">
      <Card withBorder padding="sm" bg="var(--mantine-color-gray-0)">
        <Group justify="space-between" align="center" wrap="wrap" gap="xs">
          <Text size="xs" c="dimmed" style={{ flex: 1, minWidth: 220 }}>
            Die <b>%</b> rechts zeigen, wie stark jedes Kriterium in den Score einfließt
            (Wichtigkeit, renormalisiert). „Aus" = fließt nicht ein.
          </Text>
          <Button size="xs" variant="light" onClick={fairVerteilen}>
            ⚖ Fair verteilen
          </Button>
        </Group>
      </Card>

      {/* Phasen-Schwellen: Phase-1 (→ KI-Analyse) und Phase-3 (→ Swipe-Queue) */}
      <Card withBorder padding="sm">
        <Group justify="space-between" align="center" wrap="nowrap" gap="md">
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text size="sm" fw={600}>
              🚪 Phase-1-Schwelle
            </Text>
            <Text size="xs" c="dimmed">
              Nur Inserate mit Vorscore ≥ diesem Wert kommen in Phase 2 (KI-Analyse). Höher =
              strenger, weniger KI-Aufwand.
            </Text>
          </Stack>
          <NumberInput
            value={cfg.p1Threshold ?? 0.65}
            min={0}
            max={1}
            step={0.01}
            decimalScale={2}
            fixedDecimalScale
            w={110}
            onChange={(v) => setCfg((c) => ({ ...c, p1Threshold: numOf(v) }))}
          />
        </Group>
        <Group justify="space-between" align="center" wrap="nowrap" gap="md" mt="sm">
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text size="sm" fw={600}>
              🎯 Phase-3-Schwelle (Swipe)
            </Text>
            <Text size="xs" c="dimmed">
              Nur Inserate mit (nach KI) Score ≥ diesem Wert landen in der Swipe-Queue. Höher =
              nur die Top-Treffer.
            </Text>
          </Stack>
          <NumberInput
            value={cfg.p3Threshold ?? 0.65}
            min={0}
            max={1}
            step={0.01}
            decimalScale={2}
            fixedDecimalScale
            w={110}
            onChange={(v) => setCfg((c) => ({ ...c, p3Threshold: numOf(v) }))}
          />
        </Group>
      </Card>

      {/* Preis — je nach Deal-Typ */}
      {isKauf ? (
        <>
          <Block
            title="💶 Kaufpreis"
            hint="Volle Punkte bis Wunsch, 0,5 bei Ziel, 0 + Killcrit ab Max."
            importance={cfg.kaufpreis.importance}
            onImportance={(v) => up('kaufpreis', { importance: v })}
            pct={pctOf('kaufpreis')}
          >
            <Group grow>
              <NumberInput
                label="Wunschpreis (€)"
                value={cfg.kaufpreis.comfortable}
                min={0}
                step={10000}
                thousandSeparator="."
                decimalSeparator=","
                onChange={(v) => up('kaufpreis', { comfortable: numOf(v) })}
              />
              <NumberInput
                label="Zielpreis (€, → 0,5)"
                value={cfg.kaufpreis.budget}
                min={0}
                step={10000}
                thousandSeparator="."
                decimalSeparator=","
                onChange={(v) => up('kaufpreis', { budget: numOf(v) })}
              />
              <NumberInput
                label="Maximalpreis (€, Killcrit)"
                value={cfg.kaufpreis.max || Math.round(cfg.kaufpreis.budget * cfg.kaufpreis.killFactor)}
                min={0}
                step={10000}
                thousandSeparator="."
                decimalSeparator=","
                onChange={(v) => up('kaufpreis', { max: numOf(v) })}
              />
            </Group>
          </Block>
          <Block
            title="📊 Preis pro m²"
            hint="Tipp unter ideal, 0 Punkte ab max."
            importance={cfg.pricePerSqm.importance}
            onImportance={(v) => up('pricePerSqm', { importance: v })}
            pct={pctOf('pricePerSqm')}
          >
            <Group grow>
              <NumberInput label="ideal (€/m²)" value={cfg.pricePerSqm.ideal} min={0} step={250} onChange={(v) => up('pricePerSqm', { ideal: numOf(v) })} />
              <NumberInput label="max (€/m²)" value={cfg.pricePerSqm.max} min={0} step={250} onChange={(v) => up('pricePerSqm', { max: numOf(v) })} />
            </Group>
          </Block>
        </>
      ) : (
        <Block
          title="💶 Warmmiete"
          hint="Volle Punkte bis Wunsch, 0,5 bei Ziel, 0 + Killcrit ab Max."
          importance={cfg.preis.importance}
          onImportance={(v) => up('preis', { importance: v })}
          pct={pctOf('preis')}
        >
          <Group grow>
            <NumberInput label="Wunschpreis (€)" value={cfg.preis.comfortable} min={0} step={50} onChange={(v) => up('preis', { comfortable: numOf(v) })} />
            <NumberInput label="Zielpreis (€, → 0,5)" value={cfg.preis.budget} min={0} step={50} onChange={(v) => up('preis', { budget: numOf(v) })} />
            <NumberInput label="Maximalpreis (€, Killcrit)" value={cfg.preis.max || Math.round(cfg.preis.budget * cfg.preis.killFactor)} min={0} step={50} onChange={(v) => up('preis', { max: numOf(v) })} />
          </Group>
        </Block>
      )}

      <Block title="📐 Wohnfläche (m²)" hint="Killcrit unter Minimum." importance={cfg.wohnflaeche.importance} onImportance={(v) => up('wohnflaeche', { importance: v })} pct={pctOf('wohnflaeche')}>
        <Group grow>
          <NumberInput label="Min" value={cfg.wohnflaeche.min} min={0} step={5} onChange={(v) => up('wohnflaeche', { min: numOf(v) })} />
          <NumberInput label="Gut" value={cfg.wohnflaeche.good} min={0} step={5} onChange={(v) => up('wohnflaeche', { good: numOf(v) })} />
          <NumberInput label="Ideal" value={cfg.wohnflaeche.ideal} min={0} step={5} onChange={(v) => up('wohnflaeche', { ideal: numOf(v) })} />
        </Group>
      </Block>

      <Block title="🚪 Zimmer" hint="Killcrit unter Minimum." importance={cfg.zimmer.importance} onImportance={(v) => up('zimmer', { importance: v })} pct={pctOf('zimmer')}>
        <Group grow>
          <NumberInput label="Min" value={cfg.zimmer.min} min={1} step={1} onChange={(v) => up('zimmer', { min: numOf(v) })} />
          <NumberInput label="Ideal" value={cfg.zimmer.ideal} min={1} step={1} onChange={(v) => up('zimmer', { ideal: numOf(v) })} />
        </Group>
      </Block>

      <Block title="🏗️ Baujahr" hint="Volle Punkte ab diesem Jahr." importance={cfg.baujahr.importance} onImportance={(v) => up('baujahr', { importance: v })} pct={pctOf('baujahr')}>
        <NumberInput label="ab Baujahr" value={cfg.baujahr.minYear} min={1900} max={2035} step={1} onChange={(v) => up('baujahr', { minYear: numOf(v) })} />
      </Block>

      <Block title="🌿 Außenbereich" importance={cfg.aussenbereich.importance} onImportance={(v) => up('aussenbereich', { importance: v })} pct={pctOf('aussenbereich')}>
        <Switch
          label="Pflicht — ohne Balkon/Terrasse/Garten ausschließen"
          checked={cfg.aussenbereich.required}
          onChange={(e) => up('aussenbereich', { required: e.currentTarget.checked })}
        />
        <NumberInput label="Mindest-Außenfläche (m², 0 = egal)" value={cfg.aussenbereich.minSqm} min={0} step={1} onChange={(v) => up('aussenbereich', { minSqm: numOf(v) })} />
      </Block>

      <Block title="🚌 Pendel zur Arbeit (Min)" hint="Zu deinem Primär-Anker. 0 = ignorieren." importance={cfg.pendelArbeit.importance} onImportance={(v) => up('pendelArbeit', { importance: v })} pct={pctOf('pendelArbeit')}>
        <Group grow>
          <NumberInput label="Ideal (bis)" value={cfg.pendelArbeit.ideal} min={0} step={5} onChange={(v) => up('pendelArbeit', { ideal: numOf(v) })} />
          <NumberInput label="Max (Killcrit)" value={cfg.pendelArbeit.kill} min={0} step={5} onChange={(v) => up('pendelArbeit', { kill: numOf(v) })} />
        </Group>
      </Block>

      <Block title="🚇 ÖV-Nähe (Gehminuten zur U-/S-Bahn)" hint="Geschätzt aus Luftlinie zur nächsten Station." importance={cfg.oevNaehe.importance} onImportance={(v) => up('oevNaehe', { importance: v })} pct={pctOf('oevNaehe')}>
        <Group grow>
          <NumberInput label="Ideal (bis Min)" value={cfg.oevNaehe.ideal} min={0} step={1} onChange={(v) => up('oevNaehe', { ideal: numOf(v) })} />
          <NumberInput label="Max (Min)" value={cfg.oevNaehe.maxWalkMin} min={1} step={1} onChange={(v) => up('oevNaehe', { maxWalkMin: numOf(v) })} />
        </Group>
      </Block>

      <Block title="⚡ Energieklasse" importance={cfg.energieklasse.importance} onImportance={(v) => up('energieklasse', { importance: v })} pct={pctOf('energieklasse')} />

      {has('befristung') && (
        <Block title="📄 Unbefristet" importance={cfg.befristung.importance} onImportance={(v) => up('befristung', { importance: v })} pct={pctOf('befristung')} />
      )}
      {has('abloese') && (
        <Block title="💸 Ablöse (niedrig = besser)" importance={cfg.abloese.importance} onImportance={(v) => up('abloese', { importance: v })} pct={pctOf('abloese')} />
      )}
      {has('pendelEltern') && (
        <Block title="👵 Pendel zu Eltern (Min)" hint="0 = ignorieren." importance={cfg.pendelEltern.importance} onImportance={(v) => up('pendelEltern', { importance: v })} pct={pctOf('pendelEltern')} />
      )}

      {/* Lage */}
      <Stack gap={8} style={{ paddingBottom: 8, borderBottom: '1px solid rgba(15,23,42,0.06)' }}>
        <Text fw={600} size="sm">
          📍 Lage
        </Text>
        <TextInput
          label="Ausgeschlossene Wiener Bezirke (Komma-getrennt)"
          placeholder="z. B. 21, 22"
          value={excludedText}
          onChange={(e) => setExcludedText(e.currentTarget.value)}
          onBlur={commitExcluded}
        />
        <Text size="xs" c="dimmed">
          Prio-Bezirke/-Gemeinden (Ranking) folgen in einem nächsten Schritt.
        </Text>
        <ImportanceSlider value={loc.importance} onChange={(v) => setLoc((l) => ({ ...l, importance: v }))} pct={pctOf('location')} />
      </Stack>

      <Stack gap={4}>
        <Text fw={600} size="sm">
          Weitere Ausschlüsse
        </Text>
        <Switch label="Sanierungsbedürftige ausschließen" checked={cfg.killZustandSanierung} onChange={(e) => setCfg((c) => ({ ...c, killZustandSanierung: e.currentTarget.checked }))} />
        <Switch label="Ab 3. Stock ohne Lift ausschließen" checked={cfg.killStockwerk3OhneLift} onChange={(e) => setCfg((c) => ({ ...c, killStockwerk3OhneLift: e.currentTarget.checked }))} />
        <Switch label="Erdgeschoss ohne Garten ausschließen" checked={cfg.killEgOhneGarten} onChange={(e) => setCfg((c) => ({ ...c, killEgOhneGarten: e.currentTarget.checked }))} />
      </Stack>

      <Group justify="flex-end">
        <Button onClick={() => saveMut.mutate()} disabled={!dirty || saveMut.isPending} loading={saveMut.isPending}>
          Speichern & neu bewerten
        </Button>
      </Group>
    </Stack>
  );
}

function ProfileCard({ profile }: { profile: SearchProfile }): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const activeMut = useMutation({
    mutationFn: (active: boolean) => updateSearchProfile(profile.id, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['search-profiles'] }),
    onError: (e) => notifications.show({ message: String(e), color: 'red' }),
  });
  const delMut = useMutation({
    mutationFn: () => deleteSearchProfile(profile.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['search-profiles'] });
      qc.invalidateQueries({ queryKey: ['objects'] });
      notifications.show({ message: 'Profil gelöscht', color: 'green' });
    },
    onError: (e) => notifications.show({ message: String(e), color: 'red' }),
  });

  return (
    <Card padding="lg" withBorder>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={6} style={{ minWidth: 0 }}>
          <Group gap={6} wrap="wrap">
            <Text fw={700}>{profile.name}</Text>
            <Badge color={profile.dealType === 'kauf' ? 'orange' : 'teal'} variant="light">
              {DEAL_LABEL[profile.dealType]}
            </Badge>
            <Badge color="gray" variant="light">
              {OBJ_LABEL[profile.objektTyp]}
            </Badge>
            <Badge color="indigo" variant="light">
              {REGION_LABEL[profile.region]}
            </Badge>
          </Group>
        </Stack>
        <Group gap="xs" wrap="nowrap">
          <Switch
            size="sm"
            checked={profile.active}
            onChange={(e) => activeMut.mutate(e.currentTarget.checked)}
            aria-label="aktiv"
          />
          <Button size="xs" variant="light" onClick={() => setOpen((o) => !o)}>
            {open ? 'Zu' : 'Bearbeiten'}
          </Button>
          <Button
            size="xs"
            variant="subtle"
            color="red"
            onClick={() => {
              if (confirm(`Profil „${profile.name}" löschen?`)) delMut.mutate();
            }}
          >
            Löschen
          </Button>
        </Group>
      </Group>
      {open && (
        <>
          <Divider my="sm" />
          <ProfileEditor profile={profile} />
        </>
      )}
    </Card>
  );
}

export function SearchProfiles(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['search-profiles'], queryFn: getSearchProfiles });

  const [name, setName] = useState('');
  const [dealType, setDealType] = useState<'miete' | 'kauf'>('miete');
  const [objektTyp, setObjektTyp] = useState<'wohnung' | 'haus' | 'grundstueck' | 'egal'>('egal');
  const [region, setRegion] = useState<'wien' | 'umland' | 'beide'>('beide');

  const createMut = useMutation({
    mutationFn: () => createSearchProfile({ name: name.trim(), dealType, objektTyp, region }),
    onSuccess: () => {
      setName('');
      qc.invalidateQueries({ queryKey: ['search-profiles'] });
      notifications.show({ message: 'Profil angelegt', color: 'green' });
    },
    onError: (e) => notifications.show({ message: String(e), color: 'red' }),
  });

  return (
    <Stack gap="xl">
      <Stack gap={4}>
        <Anchor component={Link} to="/settings" size="sm" c="dimmed">
          ← Einstellungen
        </Anchor>
        <Title order={1}>Such-Profile</Title>
        <Text c="dimmed" size="sm">
          Lege pro Suche (Miete/Kauf · Wohnung/Haus · Region) eigene Kriterien & Gewichte fest.
          Eingehende Inserate werden dem passenden aktiven Profil zugeordnet.
        </Text>
      </Stack>

      <Card padding="lg" withBorder>
        <Stack gap="sm">
          <Text fw={600} size="sm">
            Neues Such-Profil
          </Text>
          <TextInput label="Name" placeholder="z. B. Kauf Wohnung" value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <Group grow>
            <Stack gap={4}>
              <Text size="xs" c="dimmed">Deal-Typ</Text>
              <SegmentedControl value={dealType} onChange={(v) => setDealType(v as 'miete' | 'kauf')} data={[{ label: 'Miete', value: 'miete' }, { label: 'Kauf', value: 'kauf' }]} />
            </Stack>
            <Stack gap={4}>
              <Text size="xs" c="dimmed">Objekttyp</Text>
              <SegmentedControl value={objektTyp} onChange={(v) => setObjektTyp(v as 'wohnung' | 'haus' | 'grundstueck' | 'egal')} data={[{ label: 'Wohnung', value: 'wohnung' }, { label: 'Haus', value: 'haus' }, { label: 'Grundstück', value: 'grundstueck' }, { label: 'egal', value: 'egal' }]} />
            </Stack>
          </Group>
          <Stack gap={4}>
            <Text size="xs" c="dimmed">Region</Text>
            <SegmentedControl value={region} onChange={(v) => setRegion(v as 'wien' | 'umland' | 'beide')} data={[{ label: 'Wien', value: 'wien' }, { label: 'Umland', value: 'umland' }, { label: 'beide', value: 'beide' }]} />
          </Stack>
          <Group justify="flex-end">
            <Button onClick={() => createMut.mutate()} disabled={!name.trim() || createMut.isPending} loading={createMut.isPending}>
              Anlegen
            </Button>
          </Group>
        </Stack>
      </Card>

      {isLoading && (
        <Center py="xl">
          <Loader />
        </Center>
      )}
      <Stack gap="md">
        {data?.profiles.map((p) => (
          <ProfileCard key={p.id} profile={p} />
        ))}
        {data && data.profiles.length === 0 && (
          <Text c="dimmed" size="sm">
            Noch keine Profile — leg oben dein erstes an.
          </Text>
        )}
      </Stack>
    </Stack>
  );
}
